import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { tap } from "rxjs/operators";
import { patch, removeItem } from "@ngxs/store/operators";
import { BaseTestItemDto } from "src/app/models/dto/Base-Test-item-dto";
import { EeseCoverageApiService } from "src/app/services/api/eese-coverage-api.service";
import { EeseCoverageActions } from "./eeseCoverage.actions";
import { EeseCoverageStateModel } from "./eeseCoverage.state.model";

@State<EeseCoverageStateModel>({
  name: 'eeseCoverage',
  defaults: {
    eeseCoverages: [],
    myEeseCoverages: []
  },
})
@Injectable()
export class EeseCoverageState {
  constructor(private eeseCoverageApiService: EeseCoverageApiService) {}

  @Action(EeseCoverageActions.SetAllEeseCoverages)
  SetAllEeseCoverages(ctx: StateContext<EeseCoverageStateModel>) {
    const state = ctx.getState();
    // Don't update state if we have already downloaded items
    if (state.eeseCoverages !== undefined && state.eeseCoverages.length > 0) return EMPTY;

    return this.eeseCoverageApiService.getAll('').pipe(
      tap(eeseCoverages => {
        ctx.patchState({ eeseCoverages: eeseCoverages });
      })
    );
  }

  @Action(EeseCoverageActions.SetMyEeseCoverages)
  SetMyEeseCoverages(ctx: StateContext<EeseCoverageStateModel>, action: EeseCoverageActions.SetMyEeseCoverages) {
    const state = ctx.getState();
    // Don't update state if we have already downloaded items
    if (state.myEeseCoverages !== undefined && state.myEeseCoverages.length > 0) return EMPTY;

    return this.eeseCoverageApiService.getAll(action.userId).pipe(
      tap(eeseCoverages => {
        ctx.patchState({ myEeseCoverages: eeseCoverages });
      })
    );
  }

  @Action(EeseCoverageActions.DeleteEeseCoverage)
  DeleteEeseCoverage(ctx: StateContext<EeseCoverageStateModel>, action: EeseCoverageActions.DeleteEeseCoverage) {
    return this.eeseCoverageApiService.delete(action.eeseCoverageUid, action.regBy).pipe(
      tap(result => {
        ctx.setState(
          patch<EeseCoverageStateModel>({
            eeseCoverages: removeItem<BaseTestItemDto>(x => x.uid === action.eeseCoverageUid),
            myEeseCoverages: removeItem<BaseTestItemDto>(x => x.uid === action.eeseCoverageUid)
          })
        )
      })
    );
  }

  @Action(EeseCoverageActions.UpdateItem)
  updateItem(ctx: StateContext<EeseCoverageStateModel>, action: EeseCoverageActions.UpdateItem) {
    const state = ctx.getState();

    // Update in eeseCoverages
    const allIdx = state.eeseCoverages.findIndex(item => item.uid === action.item.uid);
    if (allIdx >= 0) {
      const updated = [...state.eeseCoverages];
      updated[allIdx] = action.item;  // Full replace - action.item is already complete
      ctx.patchState({ eeseCoverages: updated });
    }

    // Update in myEeseCoverages
    const myIdx = state.myEeseCoverages.findIndex(item => item.uid === action.item.uid);
    if (myIdx >= 0) {
      const updated = [...state.myEeseCoverages];
      updated[myIdx] = action.item;  // Full replace - action.item is already complete
      ctx.patchState({ myEeseCoverages: updated });
    }
  }

  @Action(EeseCoverageActions.ClearCache)
  clearCache(ctx: StateContext<EeseCoverageStateModel>) {
    ctx.patchState({
      eeseCoverages: [],
      myEeseCoverages: []
    });
  }

  @Selector()
  static getAllEeseCoverages(state: EeseCoverageStateModel) {
    return state.eeseCoverages;
  }

  @Selector()
  static getMyEeseCoverages(state: EeseCoverageStateModel) {
    return state.myEeseCoverages;
  }
}
